import { PiCircleNotch, PiArrowClockwise, PiTrash } from 'react-icons/pi'

import dialogConfirm from 'components/dialogConfirm'
import { CampaignColumn } from 'services/campaignColumns'
import { useDeleteCampaignColumn } from 'services/campaignColumns/hooks'

import { ApproveButton } from './ApproveButton'
import { MANUAL_COLUMN_AGENT_TYPE } from './agentTypeMetadata'

interface Props {
  column: CampaignColumn
  campaignId: number
  isRecomputing?: boolean
  onRecompute: (includeEdited: boolean) => void
}

const PREVIEW_ROW_COUNT = 5 // mirrors server/campaign_columns/constants.py

function buildIndicator(column: CampaignColumn): string {
  const { computed, total_contacts: total, failed } = column.progress

  // PREVIEW: only the first N rows were dispatched. Frame progress against the
  // preview slice, not the full campaign — the rest is gated on Approve.
  if (column.status === 'PREVIEW') {
    const previewTarget = Math.min(PREVIEW_ROW_COUNT, total)
    // Recompute may populate beyond the preview target while still in PREVIEW.
    // Cap displayed count so we never show "17/5 ✓".
    const previewComputed = Math.min(computed, previewTarget)
    const previewFailed = Math.min(failed, previewTarget - previewComputed)
    const previewDone = previewComputed + previewFailed >= previewTarget
    const remaining = Math.max(0, total - previewTarget)
    if (previewDone) {
      const head =
        previewFailed > 0
          ? `Preview · ${previewComputed}/${previewTarget} ✓ • ${previewFailed} failed`
          : `Preview · ${previewComputed}/${previewTarget} ✓`
      return remaining > 0 ? `${head} • +${remaining} more` : head
    }
    return `Preview · ${previewComputed}/${previewTarget} computing…`
  }

  const allDone = computed + failed === total
  if (!allDone) return `${computed}/${total} computing…`
  return failed > 0
    ? `${computed}/${total} ✓ • ${failed} failed`
    : `${computed}/${total} ✓`
}

export function AIColumnHeader({
  column,
  campaignId,
  isRecomputing = false,
  onRecompute,
}: Props) {
  const deleteMutation = useDeleteCampaignColumn(campaignId)
  const isManualColumn = column.engine_type === MANUAL_COLUMN_AGENT_TYPE

  const handleRecompute = async () => {
    const isPreview = column.status === 'PREVIEW'
    const confirmed = await dialogConfirm(
      isPreview
        ? 'This will regenerate AI values for the preview rows. Any cells you edited manually will be kept.'
        : 'This will regenerate AI values for unedited rows in this column. Any cells you edited manually will be kept.',
      isPreview
        ? `Rerun preview for "${column.name}"?`
        : `Rerun "${column.name}"?`,
      'AI-generated values may change after rerunning.',
      { confirmLabel: isPreview ? 'Rerun Preview' : 'Rerun Column' }
    )

    if (confirmed) {
      onRecompute(false)
    }
  }

  const handleDelete = async () => {
    const confirmed = await dialogConfirm(
      `Cell values for all contacts will be lost.`,
      `Delete column "${column.name}"?`,
      'This action cannot be undone.',
      { confirmLabel: 'Delete', buttonVariant: 'danger' }
    )
    if (confirmed) {
      deleteMutation.mutate(column.id)
    }
  }

  return (
    <div className="relative z-10 flex w-full flex-col gap-1">
      <div className="flex items-center gap-1.5">
        <span className="truncate font-medium normal-case" title={column.name}>
          {column.name}
        </span>
        <span
          className="inline-flex shrink-0 items-center rounded-2xl bg-neutral-100 px-2 py-0.5 text-xxs font-medium text-neutral-700"
          style={{ letterSpacing: '-0.2px' }}
        >
          {isManualColumn ? 'Manual' : 'AI'}
        </span>
        <span className="ml-auto flex shrink-0 items-center gap-1">
          {!isManualColumn && (
            <button
              type="button"
              onClick={handleRecompute}
              disabled={isRecomputing}
              title={isRecomputing ? 'Recomputing…' : 'Rerun AI values'}
              className="inline-flex items-center justify-center rounded p-1 text-[#6b7280] transition-colors hover:text-neutral-900 disabled:opacity-50"
            >
              {isRecomputing ? (
                <PiCircleNotch className="h-4 w-4 animate-spin" />
              ) : (
                <PiArrowClockwise className="h-4 w-4" />
              )}
            </button>
          )}
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
            title="Delete this column"
            className="inline-flex items-center justify-center rounded p-1 text-[#6b7280] transition-colors hover:text-error disabled:opacity-50"
          >
            <PiTrash className="h-4 w-4" />
          </button>
        </span>
      </div>
      <div className="flex items-center gap-2">
        <span
          className="truncate text-xxs font-normal text-fg-secondary"
          title={
            isManualColumn
              ? 'Fill values manually'
              : isRecomputing
                ? 'Recomputing…'
                : buildIndicator(column)
          }
        >
          {isManualColumn
            ? 'Fill values manually'
            : isRecomputing
              ? 'Recomputing…'
              : buildIndicator(column)}
        </span>
        {!isManualColumn && column.status === 'PREVIEW' && (
          <span className="ml-auto shrink-0">
            <ApproveButton campaignId={campaignId} columnId={column.id} />
          </span>
        )}
      </div>
    </div>
  )
}
